/**
 * Filters for levels.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { Input } from '@/components/forms/Input';
import { SearchInput } from '@/components/forms/SearchInput';
import { LEVEL_CYCLE_CONFIG, LEVEL_STATUS_CONFIG } from './level-configs';
import type { LevelFilters } from './level-configs';

/** Props for {@link LevelFiltersContent}. */
interface LevelFiltersContentProps {
  filters: LevelFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleCycle: (cycle: string) => void;
  onToggleStatus: (status: string) => void;
  onSetSearchTerm: (term: string) => void;
  onSetAgeRange: (min: number | undefined, max: number | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}


/** Toggle chips for education cycle. */
const CycleFilter: React.FC<{
  filters: LevelFilters;
  onToggleCycle: (cycle: string) => void;
}> = ({ filters, onToggleCycle }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Cycle</span>
      <div className="filter-options">
        {Object.entries(LEVEL_CYCLE_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          const isActive = filters.cycles.includes(key);
          return (
            <Button
              key={key}
              variant="custom"
              size="sm"
              icon={Icon}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                isActive ? 'filter-badge-active' : 'filter-badge-inactive',
              )}
              style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
              onClick={() => onToggleCycle(key)}
            >
              {config.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Toggle chips for level status. */
const StatusFilter: React.FC<{
  filters: LevelFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Statut</span>
      <div className="filter-options">
        {Object.entries(LEVEL_STATUS_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          const isActive = filters.statuses.includes(key);
          const color = config.badgeColor || 'rgb(var(--status-success))';
          return (
            <Button
              key={key}
              variant="custom"
              size="sm"
              icon={Icon}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                isActive ? 'filter-badge-active' : 'filter-badge-inactive',
              )}
              style={{ '--filter-color': color } as React.CSSProperties}
              onClick={() => onToggleStatus(key)}
            >
              {config.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Free-text search across level code / label. */
const SearchFilter: React.FC<{
  filters: LevelFilters;
  onSetSearchTerm: (term: string) => void;
}> = ({ filters, onSetSearchTerm }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-64">
      <SearchInput
        size="sm"
        shortcut={false}
        placeholder="Code ou libellé..."
        value={filters.searchTerm ?? ''}
        onChange={(e) => onSetSearchTerm(e.target.value)}
        onClear={() => onSetSearchTerm('')}
      />
    </div>
  </div>
);

/** Min / max age range (empty input = unbounded). */
const AgeRangeFilter: React.FC<{
  filters: LevelFilters;
  onSetAgeRange: (min: number | undefined, max: number | undefined) => void;
}> = ({ filters, onSetAgeRange }) => (
  <div className="filter-group">
    <span className="filter-group-label">Tranche d'âge</span>
    <div className="flex items-center gap-2">
      <Input
        type="number"
        inputSize="sm"
        placeholder="Min"
        className="w-20"
        value={filters.minAge ?? ''}
        onChange={(e) => {
          const val = e.target.value ? Number.parseInt(e.target.value) : undefined;
          onSetAgeRange(val, filters.maxAge);
        }}
        min={0}
        max={99}
      />
      <span className="text-sm text-muted-foreground">-</span>
      <Input
        type="number"
        inputSize="sm"
        placeholder="Max"
        className="w-20"
        value={filters.maxAge ?? ''}
        onChange={(e) => {
          const val = e.target.value ? Number.parseInt(e.target.value) : undefined;
          onSetAgeRange(filters.minAge, val);
        }}
        min={0}
        max={99}
      />
      <span className="text-sm text-muted-foreground">ans</span>
    </div>
  </div>
);


/** Ribbon of level filters; each group is gated by RBAC + configure-panel visibility. */
export const LevelFiltersContent: React.FC<LevelFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleCycle,
  onToggleStatus,
  onSetSearchTerm,
  onSetAgeRange,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-cycle') && <CycleFilter filters={filters} onToggleCycle={onToggleCycle} />}

      {show('filter-status') && <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />}

      {show('filter-search') && (
        <SearchFilter filters={filters} onSetSearchTerm={onSetSearchTerm} />
      )}

      {show('filter-age-range') && (
        <AgeRangeFilter filters={filters} onSetAgeRange={onSetAgeRange} />
      )}
    </div>
  );
};
